import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { Table as AntTable, Modal, Image } from 'antd'
import {
  Wrapper,
  PreviewContent,
  PreviewHeader,
  PreviewBody,
  PreviewFooter,
  CtaButton,
  CtaButtonsContainer
} from './ArcAd.styles'
import Search from 'components/Search'
import useAction from 'utils/useAction'
import { selectors as arcAdsSelectors } from 'domains/arcAds'
import * as actions from 'actions/views/arcAds'
import { formatArcAdForTable } from 'domains/arcAds/formatters'
import { openArcAdModal } from 'reducers/modals/actions'
import { getColumns } from './columns'

const ArcAds = () => {
  const [filters, setFilters] = useState({})
  const [sorter, setSorter] = useState({})
  const [search, setSearch] = useState('')
  const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false)
  const [selectedAdForPreview, setSelectedAdForPreview] = useState(null)
  const ads = useSelector(arcAdsSelectors.getArcAds)
  const isLoaded = useSelector(arcAdsSelectors.getIsLoaded)
  const page = useSelector(arcAdsSelectors.getPage)
  const pageCount = useSelector(arcAdsSelectors.getPageCount)
  const searchString = useSelector(arcAdsSelectors.getSearchString)
  const isSaving = useSelector(arcAdsSelectors.getIsSavingActive)
  const isFiltered = useSelector(arcAdsSelectors.getIsDataFilteredOrSorted)
  const includedData = useSelector(arcAdsSelectors.getIncludedData)
  const fetchAds = useAction(actions.fetchArcAds)
  const openArcAdModalFunc = useAction(openArcAdModal)

  useEffect(() => {
    fetchAds({ page: 1, search: '', filters: {}, sorter: {sort: 'created_at'} })
  }, [fetchAds])

  const handleTableChange = (pagination, tableFilters, tableSorter) => {
    const newPage = pagination.current
    const newFilters = tableFilters

    setFilters(newFilters)
    fetchAds({
      page: newPage,
      search: search,
      filters: newFilters,
      sorter: {sort: 'created_at'}
    })
  }

  const handleSearch = useCallback((searchValue) => {
    setSearch(searchValue)
    fetchAds({
      page: 1,
      search: searchValue,
      filters,
      sorter: {sort: 'created_at'}
    })
  }, [fetchAds, filters, sorter])


  const handlePreviewAd = (formatedAd) => {
    const ad = ads.find(({id}) => id === formatedAd.id)

    if (!ad) return

    const data = {
      bannerId: ad.attributes.banner_id,
      campaignId: ad.attributes.campaign_id,
      clickUrl: ad.attributes.click_url,
      iconUrl: ad.attributes.icon_url,
      heroUrl: ad.attributes.hero_url,
      header: ad.attributes.header,
      description: ad.attributes.description,
      ctaText: ad.attributes.cta_text,
      secondaryCtaText: ad.attributes.secondary_cta_text,
      footerText: ad.attributes.footer_text,
    }

    window.postMessage({ type: 'previewArc', data }, '*')
  }


  const handleEditAd = (ad) => {
    openArcAdModalFunc(ad)
  }

  const handlePreviewInBO = (formatedAd) => {
    const ad = ads.find(({id}) => id === formatedAd.id)
    if (!ad) return

    setSelectedAdForPreview(ad)
    setIsPreviewModalVisible(true)
  }

  const handleClosePreview = () => {
    setIsPreviewModalVisible(false)
    setSelectedAdForPreview(null)
  }


  const dataSource = ads.map(ad => formatArcAdForTable(ad, includedData))

  return (
    <Wrapper>
      <div style={{ marginBottom: 16 }}>
        <h1>Arc Ads</h1>
      </div>

      <Search
        value={search}
        onSubmit={handleSearch}
        placeholder="Search ads..."
        enterButton
        style={{ marginBottom: 16 }}
      />

      <AntTable
        columns={getColumns(handleEditAd, handlePreviewAd, handlePreviewInBO)}
        dataSource={dataSource}
        rowKey="id"
        loading={!isLoaded || isSaving}
        onChange={handleTableChange}
        pagination={{
          current: page,
          total: pageCount * 20,
          pageSize: 20,
          showQuickJumper: true,
          showSizeChanger: false,
          position: ['bottomCenter']
        }}
        scroll={{ x: 1200 }}
        size="middle"
        bordered
      />

      {/* Preview Modal */}
      <Modal
        title="Ad Preview"
        open={isPreviewModalVisible}
        onCancel={handleClosePreview}
        footer={null}
        width={600}
        centered
      >
        {selectedAdForPreview && (
          <PreviewContent>
            <PreviewHeader>
              {selectedAdForPreview.attributes?.icon_url && (
                <Image
                  src={selectedAdForPreview.attributes.icon_url}
                  alt="Ad icon"
                  style={{ width: '100%', height: 60, objectFit: 'cover', marginBottom: 16 }}
                />
              )}
              <div style={{ textAlign: 'center' }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                  {selectedAdForPreview.attributes?.header || 'No Header'}
                </h3>
                {selectedAdForPreview.attributes?.description && (
                  <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: 14 }}>
                    {selectedAdForPreview.attributes.description}
                  </p>
                )}
              </div>
            </PreviewHeader>

            <PreviewBody>
              {selectedAdForPreview.attributes?.hero_url && (
                <Image
                  src={selectedAdForPreview.attributes.hero_url}
                  alt="Ad hero"
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 8 }}
                />
              )}

              <CtaButtonsContainer>
                {selectedAdForPreview.attributes?.cta_text && (
                  <CtaButton type="primary">
                    {selectedAdForPreview.attributes.cta_text}
                  </CtaButton>
                )}
                {selectedAdForPreview.attributes?.secondary_cta_text && (
                  <CtaButton>
                    {selectedAdForPreview.attributes.secondary_cta_text}
                  </CtaButton>
                )}
                {selectedAdForPreview.attributes?.third_cta_text && (
                  <CtaButton>
                    {selectedAdForPreview.attributes.third_cta_text}
                  </CtaButton>
                )}
              </CtaButtonsContainer>
            </PreviewBody>

            {(selectedAdForPreview.attributes?.footer_text || selectedAdForPreview.attributes?.footer_policy_text) && (
              <PreviewFooter>
                {selectedAdForPreview.attributes?.footer_text && (
                  <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
                    {selectedAdForPreview.attributes.footer_text}
                  </div>
                )}
                {selectedAdForPreview.attributes?.footer_policy_text && (
                  <div style={{ fontSize: 12, color: '#999' }}>
                    {selectedAdForPreview.attributes.footer_policy_text}
                  </div>
                )}
              </PreviewFooter>
            )}
          </PreviewContent>
        )}
      </Modal>
    </Wrapper>
  )
}

export default ArcAds